import { likeCheckIn, unlikeCheckIn } from '@/api/endpoints/activity';
import { useAuth } from '@/hooks/use-auth';
import type { FeedItem, PaginatedFeedResponse, RecentLiker } from '@/types/feed';
import { useMutation, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { FEED_QUERY_KEYS } from './use-activity';

export function useLikeCheckIn() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({
      checkInId,
      currentlyLiked,
    }: {
      checkInId: string;
      currentlyLiked: boolean;
    }) => {
      if (currentlyLiked) {
        return unlikeCheckIn(checkInId);
      }
      return likeCheckIn(checkInId);
    },

    onMutate: async ({ checkInId, currentlyLiked }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });

      // Snapshot ALL feed queries for rollback
      const previousQueries = queryClient.getQueriesData<unknown>({
        queryKey: FEED_QUERY_KEYS.all,
      });

      // Build optimistic current user liker entry
      const currentUserLiker: RecentLiker | null = currentUser
        ? {
            id: currentUser.id,
            username: currentUser.username,
            displayName: currentUser.name,
            avatarUrl: currentUser.image,
          }
        : null;

      // Helper to update a single FeedItem
      const updateItem = (item: FeedItem): FeedItem => {
        if (item.checkIn.id !== checkInId) return item;

        let recentLikers = [...item.recentLikers];
        if (currentlyLiked) {
          // Remove current user from recent likers
          recentLikers = recentLikers.filter(
            (l) => l.id !== currentUser?.id
          );
        } else if (currentUserLiker) {
          // Prepend current user, cap at 3
          recentLikers = [
            currentUserLiker,
            ...recentLikers.filter((l) => l.id !== currentUser?.id),
          ].slice(0, 3);
        }

        return {
          ...item,
          userHasLiked: !currentlyLiked,
          likesCount: currentlyLiked
            ? Math.max(0, item.likesCount - 1)
            : item.likesCount + 1,
          recentLikers,
        };
      };

      // Optimistically update ALL cached feed queries
      queryClient.setQueriesData<unknown>(
        { queryKey: FEED_QUERY_KEYS.all },
        (old: unknown) => {
          if (!old) return old;

          // Infinite query shape (activity, user, menu-item, business feeds)
          if (
            typeof old === 'object' &&
            'pages' in (old as any) &&
            Array.isArray((old as any).pages)
          ) {
            const data = old as InfiniteData<PaginatedFeedResponse>;
            return {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map(updateItem),
              })),
            };
          }

          // Single FeedItem shape (check-in detail)
          if (typeof old === 'object' && 'checkIn' in (old as any)) {
            return updateItem(old as FeedItem);
          }

          return old;
        },
      );

      return { previousQueries };
    },

    onError: (_error, _variables, context) => {
      // Rollback all feed queries from snapshot
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries as [QueryKey, unknown][]) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });

  const toggleLike = (checkInId: string, currentlyLiked: boolean) => {
    mutation.mutate({ checkInId, currentlyLiked });
  };

  return {
    toggleLike,
    isLiking: mutation.isPending,
  };
}
